import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, ChevronUp, Coins, Crown, Eye, Gem, 
  Mic, Send, Star, X, Zap
} from 'lucide-react';
import {
  GridLayout,
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  ControlBar,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { LiveGiftOverlay } from './LiveGiftOverlay';
import { LiveMobileChatReactions } from './LiveMobileChatReactions';
import { LiveDesktopChatPanel } from './LiveDesktopChatPanel';
import { formatEuro } from './liveTheme';
import '@livekit/components-styles';

/* ═══════════════════════════════════════════════════════════════════════════════
   RESPONSIVE HOOK — Detect Desktop vs Mobile
═══════════════════════════════════════════════════════════════════════════════ */
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  
  useEffect(() => {
    const checkWidth = () => setIsDesktop(window.innerWidth >= 768);
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);
  
  return isDesktop;
};

/* ═══════════════════════════════════════════════════════════════════════════════
   IMMERSIVE LIVE STAGE — Premium cinematic experience
   
   Philosophy: Video dominates. UI is a whisper until needed.
   - Fullscreen video as base canvas
   - Floating glass overlays with backdrop blur
   - Minimal header with essential info
   - Chat floats over video with gradient mask
   - Quick actions as floating pills
   - Bottom dock collapses to maximize video
═══════════════════════════════════════════════════════════════════════════════ */

/* ── Pure Video Component (no UI overlays) ── */
const PureVideoRoom = ({ token, serverUrl, isHost, lang, session }) => {
  const [joined, setJoined] = useState(false);

  if (!token || !serverUrl) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-zinc-900 via-black to-black">
        <div className="text-center px-8">
          <div className="w-14 h-14 rounded-full border-2 border-white/20 border-t-white animate-spin mx-auto mb-6" />
          <p className="text-lg font-medium text-white/90">{lang === 'es' ? 'Conectando...' : 'Connecting...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 bg-black">
      <LiveKitRoom
        token={token}
        serverUrl={serverUrl}
        connect={true}
        video={isHost}
        audio={isHost}
        data-lk-theme="default"
        onConnected={() => setJoined(true)}
        className="absolute inset-0 [&_.lk-room-container]:!bg-black [&_.lk-control-bar]:hidden [&_.lk-participant-tile]:!rounded-none"
      >
        <RoomAudioRenderer />
        <VideoTracks isHost={isHost} lang={lang} session={session} />
        
        {/* Minimal host controls - floating pill */}
        {isHost && joined && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30">
            <ControlBar
              variation="minimal"
              controls={{
                microphone: true,
                camera: true,
                screenShare: true,
                leave: false,
                chat: false,
              }}
              className="[&_button]:!bg-black/60 [&_button]:!backdrop-blur-xl [&_button]:!border [&_button]:!border-white/10 [&_button]:!rounded-full [&_button]:hover:!bg-black/80 [&_button]:!w-12 [&_button]:!h-12 [&_.lk-button-group]:!gap-2 [&_.lk-control-bar]:!bg-transparent [&_.lk-control-bar]:!border-0"
            />
          </div>
        )}
        
        {/* Viewer join audio prompt */}
        {!isHost && !joined && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30">
            <button
              onClick={() => setJoined(true)}
              className="flex items-center gap-3 px-6 py-3.5 rounded-full bg-white/95 text-black font-semibold text-sm hover:bg-white transition shadow-2xl"
            >
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              {lang === 'es' ? 'Unirse al audio' : 'Join Audio'}
            </button>
          </div>
        )}
      </LiveKitRoom>
    </div>
  );
};

/* ── Video Tracks Display ── */
const VideoTracks = ({ isHost, lang, session }) => {
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  const isShow = (session?.session_format || 'room') === 'show';

  if (!tracks.length) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/50 via-black to-black" />
        <div className="relative z-10 text-center px-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500/20 to-red-500/5 mx-auto mb-6 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-red-500/20 animate-pulse flex items-center justify-center">
              <Mic size={24} className="text-red-400" />
            </div>
          </div>
          <p className="text-xl font-semibold text-white mb-2 drop-shadow-lg">
            {isHost
              ? (isShow ? (lang === 'es' ? 'Tu show está listo' : 'Your show is ready') : (lang === 'es' ? 'Tu sala está lista' : 'Your room is ready'))
              : (lang === 'es' ? 'Esperando al host...' : 'Waiting for host...')}
          </p>
          <p className="text-sm text-white/50">
            {isHost
              ? (lang === 'es' ? 'Activa cámara o micro para empezar' : 'Turn on camera or mic to start')
              : (lang === 'es' ? 'El directo aparecerá aquí' : 'The live will appear here')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <GridLayout 
      tracks={tracks} 
      className="absolute inset-0 [&_.lk-participant-tile]:!rounded-none [&_.lk-participant-tile]:!border-0 [&_.lk-participant-name]:!hidden"
    >
      <ParticipantTile />
    </GridLayout>
  );
};

/* ── Floating Header ── */
const StageHeader = ({ session, lang, onBack, isHost = false }) => {
  return (
    <div 
      data-testid="immersive-stage-header"
      className="absolute top-0 left-0 right-0 p-4 flex items-start justify-between"
      style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', zIndex: 50 }}
    >
      {/* Left: Back + LIVE badge + Name */}
      <div className="flex items-center gap-2">
        <button 
          data-testid="immersive-back-button"
          onClick={onBack}
          className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white/80 hover:bg-black/60 transition"
        >
          <ArrowLeft size={18} />
        </button>
        
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_rgba(255,59,48,0.6)]" />
            <span className="text-[11px] font-bold text-white uppercase tracking-wide">LIVE</span>
          </span>
          <span className="w-px h-3 bg-white/20" />
          <span className="text-[12px] text-white/90 font-medium truncate max-w-[120px]">
            {session?.host_name || session?.title || 'Live'}
          </span>
        </div>
      </div>

      {/* Right: Viewers only (donations hidden from public) */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md">
          <Eye size={13} className="text-white/60" />
          <span className="text-[12px] text-white/90 font-medium">{session?.viewers || 0}</span>
        </div>
        {/* Only host sees accumulated donations - private info */}
        {isHost && (session?.total_donations || 0) > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md">
            <Coins size={13} className="text-[#d4a853]" />
            <span className="text-[12px] text-white/90 font-medium">{formatEuro(session?.total_donations || 0)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Quick Tip Buttons ── */
const QuickTipOverlay = ({ onTip, token, walletBalance, lang }) => {
  const tips = [
    { amount: 2, Icon: Star, color: 'border-white/30 text-white/80' },
    { amount: 5, Icon: Gem, color: 'border-[#d4a853]/50 text-[#d4a853]' },
    { amount: 10, Icon: Crown, color: 'border-[#d4a853] text-[#d4a853] bg-[#d4a853]/10' },
  ];

  return (
    <div 
      data-testid="immersive-quick-tips"
      className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-2"
      style={{ zIndex: 50 }}
    >
      {tips.map(({ amount, Icon, color }) => (
        <button
          key={amount}
          data-testid={`quick-tip-${amount}`}
          onClick={() => token && onTip?.(amount, '')}
          disabled={!token || (walletBalance !== null && walletBalance < amount)}
          className={`w-14 h-10 rounded-full bg-black/40 backdrop-blur-md border ${color} flex items-center justify-center gap-1.5 hover:bg-black/60 active:scale-95 transition disabled:opacity-40`}
        >
          <Icon size={14} />
          <span className="text-[11px] font-bold">{amount}€</span>
        </button>
      ))}
    </div>
  );
};

/* ── Active Event Overlay (Poll/Trivia) ── */
const EventOverlay = ({ event, lang, onRespond, isHost, onClear }) => {
  if (!event) return null;
  
  const isPoll = event.type === 'poll';
  const isTrivia = event.type === 'trivia';
  const options = event.options || [];
  const totalVotes = options.reduce((sum, o) => sum + (o.votes || 0), 0);

  return (
    <div 
      data-testid="immersive-event-overlay"
      className="absolute top-20 right-4 z-30 w-64"
    >
      <div className="rounded-2xl bg-black/60 backdrop-blur-xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-[#d4a853]" />
            <span className="text-[11px] font-bold text-white/90 uppercase tracking-wide">
              {isPoll ? 'Poll' : isTrivia ? 'Trivia' : 'Event'}
            </span>
          </div>
          {isHost && (
            <button 
              onClick={onClear}
              className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition"
            >
              <X size={12} />
            </button>
          )}
        </div>
        
        {/* Question */}
        <div className="px-4 py-3">
          <p className="text-[13px] text-white font-medium mb-3">{event.prompt}</p>
          
          {/* Options */}
          <div className="space-y-2">
            {options.map((opt, i) => {
              const pct = totalVotes > 0 ? Math.round((opt.votes || 0) / totalVotes * 100) : 0;
              return (
                <button
                  key={i}
                  onClick={() => onRespond?.(opt.text)}
                  className="w-full relative px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-left hover:border-white/20 transition overflow-hidden"
                >
                  <div 
                    className="absolute inset-y-0 left-0 bg-[#d4a853]/20 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                  <div className="relative flex items-center justify-between">
                    <span className="text-[12px] text-white/90">{opt.text}</span>
                    <span className="text-[11px] text-white/50">{pct}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main Immersive Stage (Responsive) ── */
export const ImmersiveLiveStage = ({
  session,
  token,
  liveToken,
  liveUrl,
  isHost,
  lang,
  walletBalance,
  onSendChat,
  onSendTip,
  onReact,
  onRespondEvent,
  onClearEvent,
  onEndLive,
  onBack,
}) => {
  const isDesktop = useIsDesktop();

  /* ════════════════════════════════════════════════════════════════════════════
     DESKTOP LAYOUT — Twitch-style Side-by-Side (70% video / 30% chat)
     - Video fills left 70%, no overlays covering it
     - Chat panel fixed on right 30%
  ════════════════════════════════════════════════════════════════════════════ */
  if (isDesktop) {
    return (
      <div 
        data-testid="immersive-live-stage"
        data-layout="desktop"
        className="fixed inset-0 bg-black flex"
        style={{ zIndex: 9999 }}
      >
        {/* Video Section (70% width) */}
        <div className="relative flex-1 min-w-0" style={{ width: '70%' }}>
          {/* Video layer */}
          <PureVideoRoom 
            token={liveToken}
            serverUrl={liveUrl}
            isHost={isHost}
            lang={lang}
            session={session}
          />
          
          {/* Minimal gradient for header readability only */}
          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" style={{ zIndex: 10 }} />
          
          {/* Header overlay (compact for desktop) */}
          <StageHeader session={session} lang={lang} onBack={onBack} isHost={isHost} />
          
          {/* Gift overlay (centered in video area) */}
          <LiveGiftOverlay session={session} lang={lang} />
          
          {/* Host end button */}
          {isHost && (
            <button
              data-testid="immersive-end-live"
              onClick={onEndLive}
              className="absolute top-4 right-4 z-30 px-4 py-2 rounded-full bg-red-500/20 border border-red-500/40 text-red-400 text-[12px] font-semibold hover:bg-red-500/30 transition"
            >
              {lang === 'es' ? 'Terminar' : 'End'}
            </button>
          )}
        </div>

        {/* Chat Panel (30% width) — Twitch-style fixed sidebar */}
        <div className="w-[30%] min-w-[280px] max-w-[400px] h-full flex-shrink-0">
          <LiveDesktopChatPanel
            session={session}
            messages={session?.chat || []}
            onSendChat={onSendChat}
            onSendTip={onSendTip}
            onReact={onReact}
            token={token}
            lang={lang}
            walletBalance={walletBalance}
            isHost={isHost}
          />
        </div>
        
        {/* CSS for animations */}
        <style>{`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out forwards;
          }
        `}</style>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════════════════════
     MOBILE LAYOUT — TikTok-style Fullscreen with Overlay Chat
     - Video fullscreen as base canvas
     - Chat floats transparently over video
     - Reactions float on right side
  ════════════════════════════════════════════════════════════════════════════ */
  return (
    <div 
      data-testid="immersive-live-stage"
      data-layout="mobile"
      className="fixed inset-0 bg-black"
      style={{ zIndex: 9999 }}
    >
      {/* Video layer (base) */}
      <PureVideoRoom 
        token={liveToken}
        serverUrl={liveUrl}
        isHost={isHost}
        lang={lang}
        session={session}
      />
      
      {/* Gradient masks for readability */}
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/60 via-black/20 to-transparent pointer-events-none" style={{ zIndex: 10 }} />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" style={{ zIndex: 10 }} />
      
      {/* Header overlay */}
      <StageHeader session={session} lang={lang} onBack={onBack} />
      
      {/* Gift overlay */}
      <LiveGiftOverlay session={session} lang={lang} />
      
      {/* Mobile Chat & Reactions (TikTok/Instagram style) */}
      <LiveMobileChatReactions
        messages={session?.chat || []}
        onSendChat={onSendChat}
        onReact={onReact}
        token={token}
        lang={lang}
      />
      
      {/* Quick tips (left side) - Mobile only */}
      <QuickTipOverlay 
        onTip={onSendTip}
        token={token}
        walletBalance={walletBalance}
        lang={lang}
      />
      
      {/* Host end button */}
      {isHost && (
        <button
          data-testid="immersive-end-live"
          onClick={onEndLive}
          className="absolute top-4 right-4 z-30 px-4 py-2 rounded-full bg-red-500/20 border border-red-500/40 text-red-400 text-[12px] font-semibold hover:bg-red-500/30 transition"
          style={{ top: 'max(16px, env(safe-area-inset-top))', right: '16px' }}
        >
          {lang === 'es' ? 'Terminar' : 'End'}
        </button>
      )}
      
      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};
